/**
 * Central query-key factory — the single home for every server-state key.
 *
 * Why: a query and the mutations that invalidate it must use the SAME key, and
 * cross-feature dependencies (a scope write has to refresh the users list AND
 * `me`'s effective scopes) should be greppable rather than living as ad-hoc
 * string arrays scattered across hooks. Reference keys symbolically here so an
 * invalidation can't silently drift from the query it's meant to refresh.
 *
 * Keys are `as const` tuples. TanStack matches by PREFIX — invalidating a
 * shorter key invalidates everything beneath it, so never make one list's key
 * a prefix of another's unless invalidating both together is the point.
 */
export const queryKeys = {
  me: () => ['me'] as const,
  users: (tenantId: string) => ['users', tenantId] as const,
  user: (tenantId: string, userId: string) => ['user', tenantId, userId] as const,
  groups: (tenantId: string) => ['groups', tenantId] as const,
  bots: (tenantId: string) => ['bots', tenantId] as const,
  group: (tenantId: string, groupId: string) => ['group', tenantId, groupId] as const,
  apiKeys: (tenantId: string) => ['api-keys', tenantId] as const,
  apiKeyEndpoints: (tenantId: string) => ['api-keys', tenantId, 'endpoints'] as const,
  scopes: () => ['scopes'] as const,
  servicePackages: () => ['service-packages'] as const,
  tenantServicePackages: (tenantId: string) => ['service-packages', 'tenant', tenantId] as const,

  // Admin roles. Assigning one changes what the assignee's `me` returns, so a
  // write here also invalidates queryKeys.me().
  adminRoleCatalog: () => ['admin-roles'] as const,
  userAdminRoles: (userId: string) => ['admin-roles', 'user', userId] as const,
  integrations: (tenantId: string) => ['integrations', tenantId] as const,
  integrationCatalog: () => ['integrations', 'catalog'] as const,
  /** Who is signed in to one sign-in MCP connection (admin view). */
  mcpSignIns: (tenantId: string, integrationId: string) =>
    ['mcp-sign-ins', tenantId, integrationId] as const,
  /** The caller's own sign-in connections and where they stand on each. */
  mcpMyConnections: (tenantId: string, userId: string) =>
    ['mcp-my-connections', tenantId, userId] as const,
  // Capability→source state (which sources are configured/active per capability).
  // Distinct root from `integrations` so activating a source can invalidate both.
  capabilities: (tenantId: string) => ['capabilities', tenantId] as const,
  /** Usage for a period: a preset ("30d") or a custom "from..to" range. */
  usage: (period: string) => ['usage', period] as const,
  usageConversations: (period: string) => ['usage-conversations', period] as const,
  usageConversation: (conversationId: string) => ['usage-conversation', conversationId] as const,
  notificationChannels: (tenantId: string) => ['notification-channels', tenantId] as const,
  teamsAppStatus: (tenantId: string, userId: string) =>
    ['teams-app-status', tenantId, userId] as const,
  assistantPolicies: (tenantId: string) => ['assistant-policies', tenantId] as const,
  /** Pipeline events home. `event` is deliberately NOT under `events` (no prefix overlap). */
  events: (tenantId: string) => ['events', tenantId] as const,
  reportTemplates: () => ['report-templates'] as const,
  customers: () => ['customers'] as const,
  regions: () => ['regions'] as const,
  reportTemplate: (id: string) => ['report-template', id] as const,
  reportTemplateVersion: (id: string, number: number) =>
    ['report-template-version', id, number] as const,
  reportPreview: (id: string, number: number) => ['report-preview', id, number] as const,
  reportTemplateRuns: (id: string) => ['report-template-runs', id] as const,
  reportShareCandidates: (id: string) => ['report-share-candidates', id] as const,
  reportSchedules: (templateId: string) => ['report-schedules', templateId] as const,
  /** The tenant's allowlist of event types — one key for every status. */
  eventCatalog: (tenantId: string) => ['event-catalog', tenantId] as const,
  event: (tenantId: string, eventId: string) => ['event', tenantId, eventId] as const,
  workflows: (tenantId: string) => ['workflows', tenantId] as const,
  webhooks: (tenantId: string) => ['webhooks', tenantId] as const,
  assistantProfile: (tenantId: string) => ['assistant-profile', tenantId] as const,

  /** Invalidate to refresh the conversation list. */
  agentThreadsRoot: () => ['agent', 'threads'] as const,
  agentThreads: () => ['agent', 'threads', 'list'] as const,
  /** The caller's existing bot threads (one per bot). Under the threads root, so list refreshes reach it. */
  agentBotThreads: () => ['agent', 'threads', 'bots'] as const,
  /** The bots the caller has. Under `me`, so a `me` refresh reaches it. */
  myBots: () => ['me', 'bots'] as const,
  /** What one of the caller's bots watches for them. Under `me`, beside the bots. */
  whatIWatch: (botId: string) => ['me', 'bots', botId, 'what-i-watch'] as const,
  watchBudget: () => ['me', 'watch-budget'] as const,
  agentMessages: (conversationId: string) =>
    ['agent', 'thread', conversationId, 'messages'] as const,
  /** The caller's own ratings for a conversation (rehydrates the feedback control). */
  agentFeedback: (conversationId: string) =>
    ['agent', 'thread', conversationId, 'feedback'] as const,
  /** Who a conversation is shared with (owner view). */
  agentShare: (conversationId: string) => ['agent', 'thread', conversationId, 'share'] as const,
  /** Conversations shared with the caller. Siblings: the list never prefixes a read. */
  sharedWithMe: () => ['agent', 'shared', 'list'] as const,
  sharedConversation: (shareId: string) => ['agent', 'shared', 'read', shareId] as const,
  /** People search for the share picker. */
  shareCandidates: (q: string) => ['agent', 'share-candidates', q] as const,
};
